import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import { CreateDialogRequiredBlock } from 'components/create-dialog/CreateDialogRequired';
import { InfoOutlined as InfoOutlinedIcon } from 'components/icons';
import FeatureFlagged from 'components/featureflag/FeatureFlagged.tsx';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { Tooltip } from 'components/ui/tooltip';
import { isBefore } from 'date-fns';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { FeatureFlag } from 'types/features.ts';
import { cn } from 'utils/cn';
import { TimecardStatusType } from 'types/timecards';
import { getClassifierDescription, t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import SearchMyCasesCheckbox from '../form/SearchMyCasesCheckbox';
import { TimecardFormState } from './useTimecardForm';

/**
 * The case, its assignment and the activity type. In the create dialog a block holds the last two in the Required
 * section, since Finish asks for both: for the assignment through a rule it registers as it submits, for the
 * activity type through a `required` that a draft save switches off while it runs.
 */
export const CaseFields = ({ form }: { form: TimecardFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        isNewInvoiceTimecard,
        mattersFilter,
        canCreateTimecardsOnNonAccessibleMatters,
        isPartyNumberUsageEnabled,
        partyNumberPrefix,
        searchMyCases,
        setSearchMyCases,
        selectedClientName,
        showAssignmentSelectInput,
        areAllAssignmentsDisabled,
        status,
        groupedAssignmentOptions,
        assignmentGroupSortOrder,
        handleOnMatterAssignemntChange,
        initialData,
        timecardDate,
        showActivityTypeSelectInput,
        classifiers,
        activityTypeRequired,
        timecardActivityTypeOptions,
    } = form;
    const inCreateDialog = useInCreateDialog();
    const client = selectedClientName ? (
        <>
            {t('timecards.form.client')}: {selectedClientName}
        </>
    ) : null;
    // On the record page the case and the client under it are one row of the card's grid, as its read row is,
    // and the checkbox beside the label does not grow the label's line.
    const inSection = useInRecordSection();
    const searchMyCasesCheckbox = <SearchMyCasesCheckbox onChange={setSearchMyCases} value={searchMyCases} />;

    return (
        <>
            <div className={inSection ? 'form-field col-span-full' : undefined}>
                <DynamicSelectInput
                    setValue={setValue}
                    control={control}
                    label="timecards.form.case-client"
                    required
                    disabled={isNewInvoiceTimecard}
                    // An invoice's case is fixed: no clear to suggest it can change.
                    clearable={!isNewInvoiceTimecard}
                    name="matter"
                    paginated={{
                        fetch: useLazyGetMattersListQuery,
                        searchField: ['fullName', 'clientParty.name', 'clientParty.partyNumber'],
                        filters: mattersFilter,
                        sort: ['clientParty.name,asc', 'name,asc'],
                    }}
                    groupByField="clientParty.name"
                    getOptionLabel="fullName"
                    getOptionIdentifier="id"
                    placeholder="timecards.form.select-case"
                    placeholderOnFocus="timecards.form.search-case-by"
                    getSelectedValueStartAdornment={renderMatterIndicators}
                    customOptionRenderer={(option, props, getValue) =>
                        renderMatterOptionWithRestrictedSelection(
                            option,
                            props,
                            getValue,
                            canCreateTimecardsOnNonAccessibleMatters,
                            'dashboard.tooltips.timecard-blocked'
                        )
                    }
                    getGroupStartAdornment={
                        isPartyNumberUsageEnabled
                            ? (_group, options) => partyNumberPrefix(options?.[0]?.clientParty?.partyNumber)
                            : undefined
                    }
                    rightLabelComponent={
                        isNewInvoiceTimecard ? undefined : inSection ? (
                            <div className="-my-1">{searchMyCasesCheckbox}</div>
                        ) : (
                            searchMyCasesCheckbox
                        )
                    }
                    bottomRightLabelComponent={
                        // Inside the field, so the line follows it into the dialog's Required section. `w-full`:
                        // the slot is a right-aligned row, and the line reads from the left.
                        inCreateDialog && client ? (
                            <div className="text-muted-foreground w-full text-xs">{client}</div>
                        ) : undefined
                    }
                />
                {!inCreateDialog && client ? (
                    // In a section the line ends where the read value's box does, so a long name wraps alike.
                    <div className={cn('text-fg pt-1 text-sm', inSection && 'pr-[11px]')}>{client}</div>
                ) : null}
            </div>
            {showAssignmentSelectInput && (
                <FeatureFlagged action="show" flag={FeatureFlag.CASE_ASSIGNMENT_ENABLED}>
                    <CreateDialogRequiredBlock
                        // An invoice timecard has no Finish, and its Create does not ask for an assignment.
                        when={!isNewInvoiceTimecard && !areAllAssignmentsDisabled}
                        // Its own `required` is false on a draft, which a draft save relies on.
                        starred
                    >
                        <SelectInput
                            setValue={setValue}
                            control={control}
                            name="matterAssignment"
                            required={
                                !areAllAssignmentsDisabled &&
                                (status === TimecardStatusType.FINISHED || status === TimecardStatusType.DONE)
                            }
                            label="timecards.form.assignment"
                            placeholder="timecards.form.select-assignment"
                            options={groupedAssignmentOptions}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            sortDirection="asc"
                            groupSortOrder={assignmentGroupSortOrder}
                            onChange={handleOnMatterAssignemntChange}
                            getOptionDisabled={(option) => {
                                if (isNewInvoiceTimecard) {
                                    return false;
                                }
                                // Don't disable the initially loaded assignment (allow keeping historical selections)
                                const initialAssignmentId = initialData?.matterAssignment?.id;
                                if (initialAssignmentId && option.id === initialAssignmentId) {
                                    return false;
                                }
                                if (!option.endDate || !timecardDate) {
                                    return false;
                                }
                                return !isBefore(new Date(timecardDate), new Date(option.endDate));
                            }}
                        />
                    </CreateDialogRequiredBlock>
                </FeatureFlagged>
            )}
            {showActivityTypeSelectInput && (
                <CreateDialogRequiredBlock when>
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="timecards.form.activity-type"
                        tooltip={
                            <Tooltip
                                content={
                                    <div>
                                        {getClassifierDescription(classifiers['TIMECARD_ACTIVITY_TYPE'])
                                            .split('\n')
                                            .map((line, index) => {
                                                const parts = line.split('*');
                                                if (parts.length === 3) {
                                                    return (
                                                        // oxlint-disable-next-line react-doctor/no-array-index-as-key -- split lines from immutable description string; no stable id available
                                                        <p key={index} style={{ margin: 0 }}>
                                                            <strong>{parts[1]}</strong> {parts[2]}
                                                        </p>
                                                    );
                                                } else {
                                                    return (
                                                        // oxlint-disable-next-line react-doctor/no-array-index-as-key -- split lines from immutable description string; no stable id available
                                                        <p key={index} style={{ margin: 0 }}>
                                                            {line}
                                                        </p>
                                                    );
                                                }
                                            })}
                                    </div>
                                }
                                side="top"
                            >
                                <InfoOutlinedIcon className="text-grey-400 ml-[1px] h-[1.2rem]" />
                            </Tooltip>
                        }
                        name="timecardActivityType"
                        required={activityTypeRequired}
                        placeholder="timecards.form.select-activity-type"
                        options={timecardActivityTypeOptions}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        getOptionDescription="description"
                        bottomRightLabelComponent={
                            // Only where a draft can be saved: an invoice timecard's one save needs the activity
                            // type too.
                            inCreateDialog && !isNewInvoiceTimecard ? (
                                <div className="text-muted-foreground w-full text-xs">
                                    {t('create-dialog.timecard.finish-hint')}
                                </div>
                            ) : undefined
                        }
                    />
                </CreateDialogRequiredBlock>
            )}
        </>
    );
};
